import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Upload, X, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUploader({ currentImageUrl, onImageUploaded, type = 'profile', getAuthHeader }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validation
    const maxSize = 10 * 1024 * 1024; // 10 MB
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez PNG, JPG ou JPEG uniquement.');
      return;
    }

    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux. Maximum 10 MB.');
      return;
    }

    // Prévisualisation
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
      setSelectedFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/upload/profile-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Image uploadée avec succès !');
      onImageUploaded(response.data.url);
      setSelectedFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setPreviewUrl(currentImageUrl);
    setSelectedFile(null);
  };

  return (
    <Card className="border-2 border-dashed border-slate-300 hover:border-blue-400 transition-colors">
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Prévisualisation */}
          <div className="flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              {previewUrl ? (
                <img
                  src={previewUrl.startsWith('/') ? `${BACKEND_URL}${previewUrl}` : previewUrl}
                  alt="Aperçu"
                  className="w-full h-full rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border-4 border-white shadow-lg">
                  {type === 'logo' ? (
                    <Building2 className="h-16 w-16 text-blue-600" />
                  ) : (
                    <User className="h-16 w-16 text-blue-600" />
                  )}
                </div>
              )}
            </div>

            <p className="text-sm text-slate-600 text-center mb-2">
              {type === 'logo' ? 'Logo de l\'entreprise' : 'Photo de profil'}
            </p>
            <p className="text-xs text-slate-500 text-center">
              PNG, JPG ou JPEG • Max 10 MB
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="space-y-2">
            <label htmlFor="image-upload" className="block">
              <input
                id="image-upload"
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => document.getElementById('image-upload').click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir une image
              </Button>
            </label>

            {selectedFile && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
